import {
  useResendInvite,
  useUpdateUser,
  useUserTable,
} from "@features/onboarding/utility-setup/hooks";
import { UserItem } from "@features/onboarding/utility-setup/types";
import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Switch } from "@shared/ui/switch";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Edit, Link2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { userFilters } from "../../../types";
import { FilterPanel } from "../filters/filters";
import { toast } from "@shared/hooks/use-toast";

interface UserTableProps {
  onEditUser: (user: UserItem) => void;
}

const UserTable: React.FC<UserTableProps> = ({ onEditUser }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const itemsPerPage = PAGE_SIZE;

  // Parse filters from URL params
  const getFiltersFromUrl = (): userFilters => {
    const page = parseInt(searchParams.get("page") || "1");

    const urlFilters: userFilters = {
      page,
      limit: itemsPerPage,
      remote_utility_id: Number(remoteUtilityId),
    };

    // Parse status array
    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== "ALL_STATUS") {
      urlFilters.status = statusParam.split(",").map(Number);
    }

    // Parse department
    const departmentParam = searchParams.get("department");
    if (departmentParam) {
      urlFilters.department = departmentParam;
    }

    // Parse roles
    const rolesParam = searchParams.get("role");
    if (rolesParam) {
      urlFilters.role = rolesParam;
    }

    // Parse search query - Map 'query' param to 'search_data'
    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<userFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams, remoteUtilityId]);

  // Call API with filters
  const {
    data,
    isLoading: loading,
    error,
  } = useUserTable(filters, remoteUtilityId);

  const updateUserStatus = useUpdateUser();
  const resendInvite = useResendInvite();

  const handleApplyFilters = (newFilters: Partial<userFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("status");
    params.delete("department");
    params.delete("role");
    params.delete("page");

    // Apply new filters
    if (newFilters.department) {
      params.set("department", newFilters.department);
    }
    
    if (newFilters.role) {
      params.set("role", newFilters.role);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("status");
    params.delete("department");
    params.delete("role");
    params.delete("page");
    params.delete("query");

    setSearchParams(params);
  };

  // Handle user status toggle
  const handleStatusToggle = (user: any) => {
    const id = user.user.id;
    const payload = {
      id,
      payload: {
        id,
        active: !user.active,
      },
    };
    updateUserStatus.mutate(payload, {
      onSuccess: () => {
        logEvent("ONB User Status Update", {
          userId: id,
          activeStatus: !user.active,
        });
      },
    });
  };

  // Handle resend invite
  const handleResendInvite = (email: string) => {
    resendInvite.mutate({ email });
  };

  const handleCopyInviteLink = async (link?: string) => {
    if (link) {
      await navigator.clipboard.writeText(link);
      toast({
        title: "Invite link copied:",
        description: "Reset link copied to clipboard.",
      });
    } else {
      toast({ title: "Failed to copy link", variant: "destructive" });
    }
  };

  // Define table columns
  const columns: TableColumn<UserItem>[] = [
    {
      key: "name",
      header: "User",
      type: "custom",
      render: (_: any, row: UserItem) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {row.user.name}
          </div>
          <div className="text-sm text-gray-500">{row.user.email}</div>
        </div>
      ),
    },
    {
      key: "department",
      header: "Department",
      type: "custom",
      render: (_: any, row: UserItem) => (
        <span className="text-sm text-gray-900">
          {row.extraDataMap?.department?.join(", ") || "-"}
        </span>
      ),
    },
    {
      key: "role",
      header: "Role",
      type: "custom",
      render: (_: any, row: UserItem) => (
        <span className="text-sm text-gray-900">
          {row.roleCode.join(", ")}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "ACTIVE":
              return "default";
            case "INACTIVE":
              return "secondary";
            case "INVITED":
              return "outline";
            case "EXPIRED":
              return "destructive";
            default:
              return "default";
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "ACTIVE":
              return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
            case "INACTIVE":
              return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
            case "INVITED":
              return "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200";
            case "EXPIRED":
              return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
            default:
              return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
          }
        },
      },
    },
    {
      key: "createdDate",
      header: "Created",
      type: "text",
      sortable: true,
      render: (_: any, row: UserItem) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {row.user.createdDate}
          </div>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      type: "custom",
      render: (_: any, row: UserItem) => (
        <div className="flex gap-2">
          <button
            onClick={() => onEditUser(row)}
            className="text-blue-600 hover:text-blue-900 transition-colors"
            title="Edit User"
          >
            <Edit className="h-4 w-4" />
          </button>
          <Switch
            checked={row.status === "ACTIVE"}
            onCheckedChange={() => handleStatusToggle(row)}
            disabled={row.status === "EXPIRED"}
            title="Toggle User Status"
          />

          {row.status === "EXPIRED" && (
            <button
              onClick={() => handleCopyInviteLink(row?.resetUrl)}
              className="text-blue-600 hover:text-blue-900 transition-colors"
              title="Copy Invite Link"
            >
              <Link2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  // Handle search
  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm.trim());
    } else {
      params.delete(paramName);
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    setSearchParams(params);
  };

  const currentPage = parseInt(searchParams.get("page") || "1");

  return (
    <div className="space-y-4">
      {/* Generic Table */}
      <GenericTable
        data={data?.result || []}
        columns={columns}
        searchPlaceholder="Search users..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        rowKey="id"
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil((data?.count || 0) / itemsPerPage),
          totalItems: data?.count || 0,
          itemsPerPage,
          startItem: (currentPage - 1) * itemsPerPage + 1,
          endItem: Math.min(currentPage * itemsPerPage, data?.count || 0),
        }}
        onPageChange={handlePageChange}
        filterPanel={
          <FilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        emptyMessage="No users found"
      />
    </div>
  );
};

export default UserTable;